import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import SlidePanel from '../shared/SlidePanel';
import Toggle from './Toggle';
import DenominationChips from './DenominationChips';
import VoucherCard from './VoucherCard';
import { buildPreviewVoucher } from '../giftcard-shop/shopUtils';
import { AESTHETICS, getDesignsByAesthetic } from './data';
import '../shared/ui.css';
import TaxPicker from '../taxes/TaxPicker';
import LocationScope, { LOCATION_SCOPE_ALL, LOCATION_SCOPE_THIS } from '../multiLocation/LocationScope';

const StarIcon = ({ filled }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2.5l2.9 6.1 6.6.9-4.8 4.6 1.2 6.6-5.9-3.2-5.9 3.2 1.2-6.6L3.5 9.5l6.6-.9z" />
  </svg>
);

// VoucherEditorPanel — create / edit a voucher.
//
// Sections (top → bottom): Status, Basics, Denominations, Custom amount,
// Promotional pricing, Channels, Expiration, Terms & conditions, Designs.
// The Designs section is the keystone for the Bronze/Silver/Gold flow:
// the salon either offers every library design or curates a subset.
//
// Local state mirrors the voucher object. On save, we hand a clean copy
// back to the parent which decides whether to insert or replace.

const DEFAULT_VOUCHER = {
  id: null,
  name: '',
  template: 'signature',
  theme: 'charcoal',
  denominations: [25, 50, 100],
  value: 50,
  promo: null,
  // Promotional pricing — the editor speaks in (mode, value) so a salon
  // can pick percentage OR a fixed-amount discount. On save we compute
  // the legacy `promo` charge amount from the primary denomination so
  // the Vouchers table's "Promo pricing" detection keeps working.
  promoEnabled: false,
  promoMode: 'percent',  // 'percent' | 'fixed'
  promoValue: 0,
  // Online selling is OPT-IN: a new voucher never goes onto the public salon
  // page until the salon deliberately switches the online channel on.
  online: false,
  pos: true,
  sold: 0,
  status: 'active',
  expiry: '12 months',
  customAmount: false,
  customMin: 10,
  customMax: 500,
  designIds: [],
  coverDesignId: null,
  tc: '',
};

// Default T&Cs seeded into a NEW voucher. Injects the real salon name so the
// copy reads correctly out of the box; the salon can edit it freely after.
const buildDefaultTc = (salonName) =>
  `Not redeemable for cash. Valid at ${salonName || 'your salon'} only. Cannot be combined with other offers. Bookings subject to availability.`;

const EXPIRY_OPTIONS = [
  '1 month',
  '3 months',
  '6 months',
  '12 months',
  '24 months',
  'Never',
];

// Mint a stable id for new vouchers — collision-free for in-memory work,
// CTO swaps for a backend id on persistence.
const mintId = () => `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

// Promo preview sentence shown beneath the discount toggle. Uses the
// first denomination as the "example" so the salon sees the maths
// applied to their actual face values. For fixed discounts on a
// multi-denomination voucher, also mentions the smallest denomination
// since that's the edge case salons care about.
const buildPromoPreview = (form, currency) => {
  if (!form.promoEnabled || !form.denominations.length || !form.promoValue) {
    return 'Charge less than face value.';
  }
  const primary = form.denominations[0];
  if (form.promoMode === 'percent') {
    const pct = form.promoValue;
    const charge = Math.round(primary * (1 - pct / 100));
    return `${pct}% off every denomination. A ${currency}${primary} voucher costs ${currency}${charge} at checkout.`;
  }
  const off = form.promoValue;
  const sample = Math.max(0, primary - off);
  const smallest = Math.min(...form.denominations);
  if (form.denominations.length > 1 && smallest !== primary) {
    const smallestCharge = Math.max(0, smallest - off);
    return `${currency}${off} off every denomination. A ${currency}${primary} voucher costs ${currency}${sample}, a ${currency}${smallest} voucher costs ${currency}${smallestCharge}.`;
  }
  return `${currency}${off} off every denomination. A ${currency}${primary} voucher costs ${currency}${sample} at checkout.`;
};

// Build the initial form state from the voucher (or defaults for new).
// Rehydrates promo mode + value from the stored shape — if the voucher
// already carries an explicit promoMode use that, otherwise derive a
// percentage from the legacy `promo` charge amount.
const initFromVoucher = (voucher, salonName) => {
  if (!voucher) return { ...DEFAULT_VOUCHER, tc: buildDefaultTc(salonName) };
  const primary = voucher.denominations?.[0] || voucher.value || 50;
  const hasPromo = voucher.promo != null && voucher.promo < primary;
  // Prefer the explicit mode/value if the voucher already has them.
  const promoEnabled = hasPromo;
  const promoMode = voucher.promoMode || 'percent';
  let promoValue = voucher.promoValue;
  if (promoValue == null) {
    promoValue = hasPromo
      ? (promoMode === 'fixed' ? primary - voucher.promo
                                : Math.round(((primary - voucher.promo) / primary) * 100))
      : 0;
  }
  // Legacy/existing vouchers carry a single `value` but no `denominations`
  // array. Seed the chips from that value so editing one doesn't show an empty
  // "Add at least one denomination" — the value IS its denomination.
  const denominations = (voucher.denominations && voucher.denominations.length)
    ? voucher.denominations
    : (voucher.value != null ? [voucher.value] : []);
  return {
    ...DEFAULT_VOUCHER,
    ...voucher,
    denominations,
    tc: voucher.tc || buildDefaultTc(salonName),
    promoEnabled,
    promoMode,
    promoValue,
    designIds: voucher.designIds || [],
  };
};

const VoucherEditorPanel = ({
  voucher,
  designs = [],
  currency = '£',
  salonName,
  channels = { online: true, pos: true },
  onClose,
  onSave,
}) => {
  const isEdit = !!voucher;
  // A new gift card starts at every location (the chips show it); an edit
  // stays at this location unless other locations are ticked.
  const [locScope, setLocScope] = useState(isEdit ? LOCATION_SCOPE_THIS : LOCATION_SCOPE_ALL);
  const [form, setForm] = useState(() => initFromVoucher(voucher, salonName));
  // Designs section: "Curate" mode toggles between offering all designs
  // (designIds = []) and offering a curated subset (designIds populated).
  const [curating, setCurating] = useState(() => (voucher?.designIds || []).length > 0);

  const availableDesigns = useMemo(
    () => designs.filter(d => d.available),
    [designs]
  );

  const designsByAesthetic = useMemo(() => {
    const map = {};
    for (const a of AESTHETICS) {
      map[a.id] = getDesignsByAesthetic(availableDesigns, a.id);
    }
    return map;
  }, [availableDesigns]);

  // Title-card candidates — the designs this voucher actually offers (the
  // curated subset, or every available design when not curating). The salon
  // stars one of these as the cover shown to clients before they choose.
  const coverChoices = useMemo(
    () => (curating ? availableDesigns.filter(d => form.designIds.includes(d.id)) : availableDesigns),
    [curating, availableDesigns, form.designIds]
  );

  // The effective cover: the explicit pick if it's still on offer, else the
  // first candidate (matches the shop's fallback). Always star *something* so
  // the salon sees what clients will see.
  const effectiveCoverId = useMemo(() => {
    if (form.coverDesignId && coverChoices.some(d => d.id === form.coverDesignId)) return form.coverDesignId;
    return coverChoices[0] ? coverChoices[0].id : null;
  }, [form.coverDesignId, coverChoices]);

  // Design name without the leading "Aesthetic · " prefix (e.g. "Cake").
  const cleanName = (d) => (d.label || '').replace(/^[^·]+·\s*/, '');

  // Design preview. Desktop (≥980): hover/focus shows a pop-out beside the
  // panel. Mobile (no hover, full-width panel): tapping the thumbnail opens a
  // centred lightbox instead (tapped=true).
  const [preview, setPreview] = useState(null);
  const showPreview = (d, el, tapped) => {
    if (!tapped && window.innerWidth < 980) return;
    const r = el.getBoundingClientRect();
    const top = Math.min(Math.max(r.top + r.height / 2, 150), window.innerHeight - 160);
    setPreview({ design: d, name: cleanName(d), top, tapped: !!tapped });
  };
  const hidePreview = () => setPreview(null);

  // ─── Field setters ───
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const toggleDesignId = (id) => {
    const next = form.designIds.includes(id)
      ? form.designIds.filter(x => x !== id)
      : [...form.designIds, id];
    set({ designIds: next });
  };

  const bulkInAesthetic = (aestheticId, on) => {
    const ids = (designsByAesthetic[aestheticId] || []).map(d => d.id);
    const current = new Set(form.designIds);
    if (on) ids.forEach(i => current.add(i));
    else ids.forEach(i => current.delete(i));
    set({ designIds: Array.from(current) });
  };

  // Smallest denomination — used to validate that a fixed discount can't
  // exceed the cheapest voucher (which would make it free or negative).
  const smallestDenom = form.denominations.length
    ? Math.min(...form.denominations)
    : 0;

  // ─── Validation ───
  const errors = {};
  if (!form.name.trim()) errors.name = 'Voucher name is required';
  if (!form.denominations.length) errors.denominations = 'Add at least one denomination';
  if (form.customAmount && (form.customMin >= form.customMax)) {
    errors.customRange = 'Minimum must be less than maximum';
  }
  if (form.promoEnabled) {
    if (form.promoMode === 'percent') {
      if (form.promoValue < 1 || form.promoValue > 99) {
        errors.promoValue = 'Discount must be 1 to 99%';
      }
    } else {
      if (form.promoValue < 1) {
        errors.promoValue = 'Discount must be at least 1';
      } else if (smallestDenom > 0 && form.promoValue >= smallestDenom) {
        errors.promoValue = `Discount must be less than your smallest denomination (${currency}${smallestDenom})`;
      }
    }
  }
  const canSave = Object.keys(errors).length === 0;

  const handleSave = () => {
    if (!canSave) return;
    const primary = form.denominations[0] || form.value;
    // Compute the legacy `promo` charge amount from the chosen mode so
    // existing consumers (the Vouchers table's "Promo pricing" detection)
    // keep working without knowing about modes.
    let promo = null;
    if (form.promoEnabled && form.promoValue > 0) {
      promo = form.promoMode === 'percent'
        ? Math.round(primary * (1 - form.promoValue / 100))
        : primary - form.promoValue;
    }
    // Curating off → empty designIds means "every available design"
    const designIds = curating ? form.designIds : [];
    // Persist the title card only when it's still a design the voucher offers;
    // otherwise leave it null so the shop falls back to the first design.
    const coverDesignId = (form.coverDesignId && coverChoices.some(d => d.id === form.coverDesignId))
      ? form.coverDesignId
      : null;
    const out = {
      ...form,
      id: form.id || mintId(),
      value: primary,
      promo,
      designIds,
      coverDesignId,
      sold: form.sold || 0,
      // Keep promoMode + promoValue on the stored voucher so the editor
      // can rehydrate next time without losing intent.
    };
    delete out.promoEnabled;  // derived state — re-derived on next edit
    out._locations = locScope; // Locations control — read by the page after the API save
    onSave(out);
  };

  // ─── Footer ───
  const footer = (
    <>
      <button type="button" className="sp-btn-secondary" onClick={onClose}>
        Cancel
      </button>
      <button
        type="button"
        className="sp-btn-primary"
        onClick={handleSave}
        disabled={!canSave}
      >
        {isEdit ? 'Save changes' : 'Create voucher'}
      </button>
    </>
  );

  return (
    <>
    <SlidePanel
      title={isEdit ? 'Edit voucher' : 'New voucher'}
      onClose={onClose}
      footer={footer}
      width={600}
      className="voucher-v2 v2-editor-panel"
    >
      {/* AVAILABILITY — status (live vs draft) + the channels it sells on,
          grouped into one block since they're one decision: is it live,
          and where is it sold. */}
      <div className="v2-pp-section">
        <div className="v2-pp-section-header">
          <p className="beu-section-label">Availability</p>
          <Toggle
            on={form.status === 'active'}
            onChange={(on) => set({ status: on ? 'active' : 'draft' })}
            ariaLabel="Active"
          />
        </div>
        <p className="v2-pp-help">
          {form.status === 'active'
            ? 'Active. Live and sellable on the channels selected below.'
            : 'Draft. Hidden everywhere until you make it active.'}
        </p>

        <div className="v2-ed-availability-channels">
          <div className={`v2-ed-toggle-row ${channels.online ? '' : 'is-overridden'}`}>
            <div>
              <div className="v2-ed-toggle-title">Online booking page</div>
              <div className="v2-pp-help">
                {channels.online
                  ? "Listed in your storefront's gift-card section."
                  : 'Online selling is off for your whole salon, so this stays hidden online. We keep your choice for when you switch it back on.'}
              </div>
            </div>
            <Toggle
              on={form.online}
              onChange={(on) => set({ online: on })}
              ariaLabel="Online booking page"
            />
          </div>
          <div className={`v2-ed-toggle-row ${channels.pos ? '' : 'is-overridden'}`}>
            <div>
              <div className="v2-ed-toggle-title">In-store POS</div>
              <div className="v2-pp-help">
                {channels.pos
                  ? 'Sellable at the front desk via the POS.'
                  : 'In-store selling is off for your whole salon, so this stays hidden at the POS. We keep your choice for when you switch it back on.'}
              </div>
            </div>
            <Toggle
              on={form.pos}
              onChange={(on) => set({ pos: on })}
              ariaLabel="In-store POS"
            />
          </div>
        </div>
      </div>

      {/* BASICS */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Basics</p>
        <LocationScope value={locScope} onChange={setLocScope} help="Sold and shown at the chosen locations; each copy is independent afterwards." />
        <div className="v2-ed-field">
          <label className="v2-ed-label">Voucher name</label>
          <input
            type="text"
            className="sp-input"
            placeholder="e.g. House Signature"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
          />
          {errors.name && <div className="v2-ed-error">{errors.name}</div>}
          <p className="v2-pp-help">
            Internal name. Clients see your gift card designs, not this.
          </p>
        </div>
      </div>

      {/* DENOMINATIONS */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Denominations</p>
        <p className="v2-pp-help" style={{ marginTop: 4, marginBottom: 12 }}>
          Face values the client can choose from.
        </p>
        <DenominationChips
          values={form.denominations}
          currency={currency}
          onChange={(next) => set({ denominations: next })}
        />
        {errors.denominations && <div className="v2-ed-error">{errors.denominations}</div>}
      </div>

      {/* CUSTOM AMOUNT */}
      <div className="v2-pp-section">
        <div className="v2-pp-section-header">
          <p className="beu-section-label">Custom amount</p>
          <Toggle
            on={form.customAmount}
            onChange={(on) => set({ customAmount: on })}
            ariaLabel="Allow client-chosen amount"
          />
        </div>
        <p className="v2-pp-help">
          Let the client enter any value within a range you set.
        </p>
        {form.customAmount && (
          <div className="v2-ed-row">
            <div className="v2-ed-field">
              <label className="v2-ed-label">Minimum</label>
              <div className="v2-ed-input-prefix">
                <span>{currency}</span>
                <input
                  type="number"
                  className="sp-input"
                  min="1"
                  value={form.customMin}
                  onChange={(e) => set({ customMin: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
            </div>
            <div className="v2-ed-field">
              <label className="v2-ed-label">Maximum</label>
              <div className="v2-ed-input-prefix">
                <span>{currency}</span>
                <input
                  type="number"
                  className="sp-input"
                  min="1"
                  value={form.customMax}
                  onChange={(e) => set({ customMax: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
            </div>
          </div>
        )}
        {errors.customRange && <div className="v2-ed-error">{errors.customRange}</div>}
      </div>

      {/* PROMOTIONAL PRICING */}
      <div className="v2-pp-section">
        <div className="v2-pp-section-header">
          <p className="beu-section-label">Promotional pricing</p>
          <Toggle
            on={form.promoEnabled}
            onChange={(on) => set({
              promoEnabled: on,
              // Seed a sensible default when turning on so the input isn't 0
              promoValue: on
                ? (form.promoValue || (form.promoMode === 'percent' ? 15 : 10))
                : form.promoValue,
            })}
            ariaLabel="Offer a discount"
          />
        </div>
        <p className="v2-pp-help">
          {form.promoEnabled
            ? buildPromoPreview(form, currency)
            : 'Charge less than face value. Pick a percentage off every denomination, or a flat amount.'}
        </p>
        {form.promoEnabled && (
          <>
            {/* Segmented control: Percentage / Fixed amount */}
            <div
              className="v2-ed-segment"
              role="tablist"
              aria-label="Discount type"
              style={{ marginTop: 12 }}
            >
              <button
                type="button"
                role="tab"
                aria-selected={form.promoMode === 'percent'}
                className={`v2-ed-segment-btn ${form.promoMode === 'percent' ? 'active' : ''}`}
                onClick={() => set({ promoMode: 'percent', promoValue: 15 })}
              >
                Percentage
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={form.promoMode === 'fixed'}
                className={`v2-ed-segment-btn ${form.promoMode === 'fixed' ? 'active' : ''}`}
                onClick={() => set({ promoMode: 'fixed', promoValue: 10 })}
              >
                Fixed amount
              </button>
            </div>

            <div className="v2-ed-field" style={{ marginTop: 12 }}>
              <label className="v2-ed-label">Discount</label>
              {form.promoMode === 'percent' ? (
                <div className="v2-ed-input-suffix">
                  <input
                    type="number"
                    className="sp-input"
                    min="1"
                    max="99"
                    value={form.promoValue}
                    onChange={(e) => set({ promoValue: parseInt(e.target.value, 10) || 0 })}
                  />
                  <span>%</span>
                </div>
              ) : (
                <div className="v2-ed-input-prefix">
                  <span>{currency}</span>
                  <input
                    type="number"
                    className="sp-input"
                    min="1"
                    value={form.promoValue}
                    onChange={(e) => set({ promoValue: parseInt(e.target.value, 10) || 0 })}
                  />
                </div>
              )}
              {errors.promoValue && <div className="v2-ed-error">{errors.promoValue}</div>}
            </div>
          </>
        )}
      </div>


      {/* EXPIRATION */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Expiration</p>
        <p className="v2-pp-help" style={{ marginTop: 4, marginBottom: 12 }}>
          How long the recipient has to redeem once issued.
        </p>
        <select
          className="sp-select"
          value={form.expiry}
          onChange={(e) => set({ expiry: e.target.value })}
        >
          {EXPIRY_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      {/* TAX */}
      {form.id ? (
        <div className="v2-pp-section">
          <p className="beu-section-label">Tax</p>
          <TaxPicker domain="vouchers" itemId={form.id} />
        </div>
      ) : null}

      {/* TERMS & CONDITIONS */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Terms &amp; conditions</p>
        <p className="v2-pp-help" style={{ marginTop: 4, marginBottom: 12 }}>
          Shown to the recipient on the voucher email and at redemption.
        </p>
        <textarea
          className="sp-textarea"
          rows={5}
          value={form.tc}
          onChange={(e) => set({ tc: e.target.value })}
        />
      </div>

      {/* DESIGNS — pick which designs this voucher offers, preview each (hover
          for a pop-out), and star one as the "title card" (the cover shown in
          the shop before the buyer picks their own). */}
      <div className="v2-pp-section">
        <div className="v2-pp-section-header">
          <p className="beu-section-label">Designs</p>
          <Toggle
            on={curating}
            onChange={(on) => {
              setCurating(on);
              // Turning OFF curation clears the designIds list (will mean
              // "all available"). Turning ON keeps any existing list — or
              // starts empty if there was none.
              if (!on) set({ designIds: [] });
            }}
            ariaLabel="Curate designs for this voucher"
          />
        </div>
        <p className="v2-pp-help">
          {curating
            ? `Pick which designs clients can choose from, and star one as the title card they see first. ${form.designIds.length} of ${availableDesigns.length} selected.`
            : `Every design is offered (${availableDesigns.length} in your library). Star one as the title card clients see first, or curate a subset.`}
        </p>

        {availableDesigns.length === 0 ? (
          <div className="v2-pp-empty">
            No designs available in your library. Add or enable designs in the Designs tab.
          </div>
        ) : (
          <div className="v2-ed-designs">
            {AESTHETICS.map((a) => {
              const list = designsByAesthetic[a.id] || [];
              if (!list.length) return null;
              const selectedInGroup = list.filter(d => form.designIds.includes(d.id)).length;
              const allOn = selectedInGroup === list.length;
              return (
                <div key={a.id} className="v2-ed-designs-group">
                  <div className="v2-ed-designs-group-header">
                    <div className="v2-ed-designs-group-name">
                      {a.name}
                      {curating && (
                        <span className="v2-ed-designs-group-count">{selectedInGroup}/{list.length}</span>
                      )}
                    </div>
                    {curating && (
                      <button
                        type="button"
                        className="v2-ed-designs-bulk"
                        onClick={() => bulkInAesthetic(a.id, !allOn)}
                      >
                        {allOn ? 'Clear' : 'Select all'}
                      </button>
                    )}
                  </div>
                  <div className="v2-ed-designs-list">
                    {list.map((d) => {
                      const checked = form.designIds.includes(d.id);
                      const offered = curating ? checked : true;
                      const isCover = offered && effectiveCoverId === d.id;
                      const name = cleanName(d);
                      return (
                        <div
                          key={d.id}
                          className={`v2-ed-design-row ${offered ? 'is-offered' : ''} ${checked ? 'is-checked' : ''}`}
                        >
                          {curating && (
                            <input
                              type="checkbox"
                              className="v2-ed-design-check"
                              checked={checked}
                              onChange={() => toggleDesignId(d.id)}
                              aria-label={`Offer ${name}`}
                            />
                          )}
                          {/* Real card thumbnail + hover pop-out preview, so the
                              salon can actually tell the designs apart. */}
                          <button
                            type="button"
                            className="v2-ed-design-thumb"
                            onMouseEnter={(e) => showPreview(d, e.currentTarget)}
                            onMouseLeave={hidePreview}
                            onFocus={(e) => showPreview(d, e.currentTarget)}
                            onBlur={hidePreview}
                            onClick={(e) => { if (window.innerWidth < 980) showPreview(d, e.currentTarget, true); }}
                            aria-label={`Preview ${name}`}
                          >
                            <span className="v2-ed-design-thumb-card">
                              <VoucherCard
                                voucher={buildPreviewVoucher(d, form.value || 50)}
                                aesthetic={d.aesthetic}
                                salonName={salonName}
                                currency={currency}
                                showPromo={false}
                              />
                            </span>
                          </button>
                          <button
                            type="button"
                            className="v2-ed-design-label"
                            onClick={() => { if (curating) toggleDesignId(d.id); }}
                            tabIndex={curating ? 0 : -1}
                          >
                            {name}
                          </button>
                          {/* Inline title-card star — becomes a labelled pill on
                              the chosen cover so it's obvious which design clients
                              see first. */}
                          <button
                            type="button"
                            className={`v2-ed-design-star ${isCover ? 'is-cover' : ''}`}
                            onClick={() => { if (offered) set({ coverDesignId: d.id }); }}
                            disabled={!offered}
                            aria-pressed={isCover}
                            title={!offered ? 'Offer this design to feature it' : isCover ? 'Title card: the design clients see first' : 'Set as the title card'}
                          >
                            <StarIcon filled={isCover} />
                            <span className="v2-ed-design-star-tag">{isCover ? 'Title card' : 'Set as title card'}</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SlidePanel>

    {/* Design preview — desktop: a pop-out floated to the left of the panel.
        Mobile: a centred, tap-to-dismiss lightbox. */}
    {preview && ReactDOM.createPortal(
      preview.tapped ? (
        <div className="v2-ed-preview-lightbox" onClick={hidePreview}>
          <div className="voucher-v2 v2-ed-preview-lightbox-card" onClick={(e) => e.stopPropagation()}>
            <div className="v2-ed-preview-pop-card">
              <VoucherCard
                voucher={buildPreviewVoucher(preview.design, form.value || 50)}
                aesthetic={preview.design.aesthetic}
                salonName={salonName}
                currency={currency}
                showPromo={false}
              />
            </div>
            <div className="v2-ed-preview-pop-name">{preview.name}</div>
          </div>
        </div>
      ) : (
        <div className="voucher-v2 v2-ed-preview-pop" style={{ top: preview.top }}>
          <div className="v2-ed-preview-pop-card">
            <VoucherCard
              voucher={buildPreviewVoucher(preview.design, form.value || 50)}
              aesthetic={preview.design.aesthetic}
              salonName={salonName}
              currency={currency}
              showPromo={false}
            />
          </div>
          <div className="v2-ed-preview-pop-name">{preview.name}</div>
        </div>
      ),
      document.body
    )}
    </>
  );
};

export default VoucherEditorPanel;
